import { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { isAuthenticated } from './utils/auth';
import { SelectionProvider, useSelection } from './context/SelectionContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './components/common/Toast';
import { LocaleTransitionProvider } from './context/LocaleTransitionContext';
import { useTotalUnviewedCount } from './hooks/useChildrenCounts';
import { useFirstUnviewedSuggestion } from './hooks/useFirstUnviewedSuggestion';

// Initialize i18n
import './i18n';
import Login from './pages/Login';
import AutoLogin from './pages/AutoLogin';
import Dashboard from './pages/Dashboard';
import Planner from './pages/Planner';
import ResearchPortal from './pages/ResearchPortal';
import Consulting from './pages/Consulting';
import TermsOfUse from './pages/TermsOfUse';
import Privacy from './pages/Privacy';
import SuggestedCoursesPopup from './components/common/SuggestedCoursesPopup';
import BottomNav from './components/common/BottomNav';
import PageTransition from './components/common/PageTransition';
import Header from './components/layout/Header';
import BrowserSupportBanner from './components/common/BrowserSupportBanner';

const HIDE_POPUP_KEY = 'suggestions_popup_hidden_date';

// Get today's date as YYYY-MM-DD string
const getTodayString = () => new Date().toISOString().split('T')[0];

// Check if popup was hidden for today
const isHiddenForToday = () => {
  try {
    const hiddenDate = localStorage.getItem(HIDE_POPUP_KEY);
    return hiddenDate === getTodayString();
  } catch {
    return false;
  }
};

function UnviewedSuggestionsPopup() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectChild } = useSelection();
  const { totalCount } = useTotalUnviewedCount();
  const { firstChild, firstSemesterId } = useFirstUnviewedSuggestion();
  const [isTempDismissed, setIsTempDismissed] = useState(false);
  const [hiddenForToday, setHiddenForToday] = useState(isHiddenForToday);
  const prevCountRef = useRef(totalCount);

  // Reset temp dismissed state when navigating to a different page
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional reset on route change
    setIsTempDismissed(false);
  }, [location.pathname]);

  // Reset temp dismissed state when count increases (new suggestions arrived)
  useEffect(() => {
    if (totalCount > prevCountRef.current) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional reset when new suggestions arrive
      setIsTempDismissed(false);
    }
    prevCountRef.current = totalCount;
  }, [totalCount]);

  // Handle "Hide for Today" - persists until end of day
  const handleHideForToday = () => {
    try {
      localStorage.setItem(HIDE_POPUP_KEY, getTodayString());
      setHiddenForToday(true);
    } catch {
      // Fallback to temp dismiss if localStorage fails
      setIsTempDismissed(true);
    }
  };

  // Smart navigation: select first child with notifications and navigate to their semester
  const handleViewNow = useCallback(() => {
    // Select the first child with unviewed suggestions
    if (firstChild) {
      selectChild(firstChild);
    }

    // Navigate to planner with initial semester in state
    navigate('/planner', {
      state: firstSemesterId ? { initialSemesterId: firstSemesterId } : undefined,
    });

    // Dismiss the popup
    setIsTempDismissed(true);
  }, [firstChild, firstSemesterId, selectChild, navigate]);

  // Don't show popup on the planner page (where suggestions are displayed)
  // Include in isOpen logic instead of returning null to preserve enter animation
  const isOnPlannerPage = location.pathname === '/planner';
  const isOpen = totalCount > 0 && !isTempDismissed && !hiddenForToday && !isOnPlannerPage;

  return (
    <SuggestedCoursesPopup
      isOpen={isOpen}
      onDismiss={() => setIsTempDismissed(true)}
      onHideForToday={handleHideForToday}
      onViewNow={handleViewNow}
      unviewedCount={totalCount}
    />
  );
}

function ProtectedLayout() {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return (
    <SelectionProvider>
      <div className="h-dvh flex flex-col overflow-hidden pb-16 sm:pb-0 bg-gray-50 dark:bg-gray-900 transition-colors">
        {/* Header stays static during page transitions */}
        <Header />
        {/* Only content below header transitions */}
        <PageTransition>
          <Outlet />
        </PageTransition>
      </div>
      <BottomNav />
      <UnviewedSuggestionsPopup />
    </SelectionProvider>
  );
}

function PublicRoute({ children }) {
  if (isAuthenticated()) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <LocaleTransitionProvider>
        <BrowserSupportBanner />
        <ToastProvider>
          <BrowserRouter>
            <Routes>
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route path="/auto-login" element={<AutoLogin />} />
              {/* Protected routes share SelectionProvider */}
              <Route element={<ProtectedLayout />}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/planner" element={<Planner />} />
                <Route path="/research" element={<ResearchPortal />} />
                <Route path="/consulting" element={<Consulting />} />
              </Route>
              <Route path="/tos" element={<TermsOfUse />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </ToastProvider>
      </LocaleTransitionProvider>
    </ThemeProvider>
  );
}

export default App;
